import { FleetDocumentUpload } from "../documents/types";
import { FileExtended } from "ui/components/FileUpload/FileDropZone/types";
import { FleetVehicleOfferRent, FleetVehicleOfferRentEditFormValues, FleetVehicleOfferRentItemChildRow, FleetVehicleOfferRentItemEditFormValues, FleetVehicleOfferRentItemRow, FleetVehicleOfferRentRow, OfferRentStatus, FleetVehicleOfferSale, FleetVehicleOfferSaleEditFormValues, FleetVehicleOfferSaleItemChildRow, FleetVehicleOfferSaleItemEditFormValues, FleetVehicleOfferSaleItemRow, FleetVehicleOfferSaleRow, OfferSaleStatus } from "./types";


export const transformFleetVehicleOfferSaleRow = (
  res: any
): FleetVehicleOfferSaleRow => {
  return {
    id: res.id,
    offerNumber: res.offerNumber,
    status: res.status ? transformOfferSaleStatus(res.status) : null,
    vehicle: res.vehicle,
    contactClient: res.contactClient,
    offerDate: res.offerDate,
    dueDate: res.dueDate,
    durationDays: res.durationDays,
    total: res.total,
    totalWithoutTax: res.totalWithoutTax ?? null,
    totalTax: res.totalTax ?? null,
    totalDiscount: res.totalDiscount ?? null,
    userCreatedName: res.userCreatedName ?? null,
    pdfUpload: res.pdfUpload ?? null,
  };
};


export const transformFleetVehicleOfferSale = (
  res: any
): FleetVehicleOfferSale => ({
  id: res.id,
  offerNumber: res.offerNumber,
  status: transformOfferSaleStatus(res.status),
  vehicle: res.vehicle,
  contactClient: res.contactClient,
  contactConsumer: res.contactConsumer,
  offerDate: res.offerDate,
  dueDate: res.dueDate,
  durationDays: res.durationDays,
  total: res.total,
  items: res.items,
  note: res.note,
  pdfUpload: res.pdfUpload,
  statusHistory: res.statusHistory ?? [],
});




export const transformFleetVehicleOfferSaleEditForm = (
  res: FleetVehicleOfferSale
): FleetVehicleOfferSaleEditFormValues => ({
  id: res.id,
  offerNumber: res.offerNumber ?? null,
  offerSaleStatusId: res.status?.id ?? null,
  vehicleId: res.vehicle?.id ?? null,
  contactClientId: res.contactClient?.id ?? null,
  contactConsumerId: res.contactConsumer?.id ?? null,
  offerDate: res.offerDate,
  dueDate: res.dueDate,
  durationDays: res.durationDays,
  note: res.note ?? null,
  items: res.items
});


export const transformFleetVehicleOfferSaleOnCreate = (
  formValues: FleetVehicleOfferSaleEditFormValues
): any => ({
  offerNumber: formValues.offerNumber,
  offerSaleStatusId: formValues.offerSaleStatusId,
  contactClientId: formValues.contactClientId,
  contactConsumerId: formValues.contactConsumerId,
  offerDate: formValues.offerDate,
  dueDate: formValues.dueDate,
  durationDays: formValues.durationDays,
  note: formValues.note,
  items: formValues.items
});

export const transformFleetVehicleOfferSaleOnUpdate = (
  offerId: string,
  formValues: FleetVehicleOfferSaleEditFormValues,
): any => ({
  id: offerId,
  offerSaleStatusId: formValues.offerSaleStatusId,
  vehicleId: formValues.vehicleId,
  contactClientId: formValues.contactClientId,
  contactConsumerId: formValues.contactConsumerId,
  offerDate: formValues.offerDate,
  dueDate: formValues.dueDate,
  durationDays: formValues.durationDays,
  note: formValues.note,
  items: formValues.items
});



export const transformOfferSaleItemRow = (
  res: FleetVehicleOfferSaleItemEditFormValues
): FleetVehicleOfferSaleItemRow => {
  return {
    id: res.id ? res.id : crypto.randomUUID(),
    code: res.code,
    name: res.name,
    taxRate: res.taxRate,
    quantity: res.quantity,
    priceWithoutTax: res.priceWithoutTax,
    priceWithTax: res.priceWithTax,
    discountAmount: res.discountAmount,
    discountTypeId: res.discountTypeId,
    total: res.total,
    deleted: false,
    billingItemTypeId: res.billingItemTypeId,
    billingItemTypeName: res.billingItemTypeName ?? null,
    kpdCode: res.kpdCode,
    unitOfMeasureId: res.unitOfMeasureId,
    note: res.note,
    childRows: res.note
      ? [transformOfferSaleItemChildRow(res)]
      : [],
  };
};

export const transformOfferSaleItemChildRow = (
  res: FleetVehicleOfferSaleItemEditFormValues
): FleetVehicleOfferSaleItemChildRow => {
  return {
    note: res.note,
  };
};


export const transformOfferSaleItemRowToForm = (
  row: FleetVehicleOfferSaleItemRow
): FleetVehicleOfferSaleItemEditFormValues => {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    taxRate: row.taxRate,
    quantity: row.quantity,
    priceWithoutTax: row.priceWithoutTax,
    priceWithTax: row.priceWithTax,
    discountAmount: row.discountAmount,
    discountTypeId: row.discountTypeId,
    total: row.total,
    note: row.note ?? row.childRows?.[0]?.note ?? null,
    billingItemTypeId: row.billingItemTypeId,
    billingItemTypeName: row.billingItemTypeName ?? null,
    kpdCode: row.kpdCode,
    unitOfMeasureId: row.unitOfMeasureId,
  };
};

export const transformOfferSaleStatus = (res: any): OfferSaleStatus => ({
  id: res.id,
  name: res.name,
  color: res.color,
});

export const transformFleetDocumentUploadToFileExtended = (
  upload: FleetDocumentUpload
): FileExtended => {
  const blob = new Blob([], { type: upload.contentType });

  return {
    id: upload.id,
    name: upload.originalFileName,
    url: upload.url,
    title: upload.originalFileName,
    isNew: false,
    size: upload.fileSizeKb * 1024,
    type: upload.contentType,
    lastModified: new Date(upload.created).getTime(),
    arrayBuffer: () => blob.arrayBuffer(),
    slice: (start, end, contentType) => blob.slice(start, end, contentType),
    stream: () => blob.stream(),
    text: () => blob.text(),
    webkitRelativePath: "",
  } as FileExtended;
};

export const transformPdfPreviewToFileExtended = (
  data: any,
  fileName: string
): FileExtended => {
  const blob = new Blob([data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);

  return {
    id: crypto.randomUUID(),
    name: fileName,
    url: url,
    title: fileName,
    isNew: false,
    size: blob.size,
    type: "application/pdf",
    lastModified: Date.now(),
    arrayBuffer: () => blob.arrayBuffer(),
    slice: (start, end, contentType) => blob.slice(start, end, contentType),
    stream: () => blob.stream(),
    text: () => blob.text(),
    webkitRelativePath: "",
  } as FileExtended;
};

export const transformOfferRentStatus = (res: any): OfferRentStatus => ({
  id: res.id,
  name: res.name,
  color: res.color,
});

export const transformFleetVehicleOfferRentRow = (
  res: any
): FleetVehicleOfferRentRow => {
  return {
    id: res.id,
    offerNumber: res.offerNumber,
    status: res.status ? transformOfferRentStatus(res.status) : null,
    vehicle: res.vehicle,
    contactClient: res.contactClient,
    offerDate: res.offerDate,
    dueDate: res.dueDate,
    durationDays: res.durationDays,
    total: res.total,
    totalWithoutTax: res.totalWithoutTax ?? null,
    totalTax: res.totalTax ?? null,
    totalDiscount: res.totalDiscount ?? null,
    userCreatedName: res.userCreatedName ?? null,
    rentStartDate: res.rentStartDate,
    rentEndDate: res.rentEndDate,
    rentDurationMonths: res.rentDurationMonths,
    monthlyPrice: res.monthlyPrice,
    pdfUpload: res.pdfUpload ?? null,
  };
};

export const transformFleetVehicleOfferRent = (
  res: any
): FleetVehicleOfferRent => ({
  id: res.id,
  offerNumber: res.offerNumber,
  status: transformOfferRentStatus(res.status),
  vehicle: res.vehicle,
  contactClient: res.contactClient,
  contactConsumer: res.contactConsumer,
  offerDate: res.offerDate,
  dueDate: res.dueDate,
  durationDays: res.durationDays,
  total: res.total,
  note: res.note,
  rentStartDate: res.rentStartDate,
  rentEndDate: res.rentEndDate,
  rentDurationMonths: res.rentDurationMonths,
  monthlyPrice: res.monthlyPrice,
  items: res.items,
  pdfUpload: res.pdfUpload,
  statusHistory: res.statusHistory ?? [],
});

export const transformFleetVehicleOfferRentOnCreate = (
  formValues: FleetVehicleOfferRentEditFormValues
): any => ({
  offerNumber: formValues.offerNumber,
  offerRentStatusId: formValues.offerRentStatusId,
  contactClientId: formValues.contactClientId,
  contactConsumerId: formValues.contactConsumerId,
  offerDate: formValues.offerDate,
  dueDate: formValues.dueDate,
  durationDays: formValues.durationDays,
  note: formValues.note,
  rentStartDate: formValues.rentStartDate,
  rentEndDate: formValues.rentEndDate,
  rentDurationMonths: formValues.rentDurationMonths,
  monthlyPrice: formValues.monthlyPrice,
  items: formValues.items,
});

export const transformFleetVehicleOfferRentEditForm = (
  res: FleetVehicleOfferRent
): FleetVehicleOfferRentEditFormValues => ({
  id: res.id,
  offerNumber: res.offerNumber ?? null,
  offerRentStatusId: res.status?.id ?? null,
  vehicleId: res.vehicle?.id ?? null,
  contactClientId: res.contactClient?.id ?? null,
  contactConsumerId: res.contactConsumer?.id ?? null,
  offerDate: res.offerDate,
  dueDate: res.dueDate,
  durationDays: res.durationDays,
  rentStartDate: res.rentStartDate,
  rentEndDate: res.rentEndDate,
  rentDurationMonths: res.rentDurationMonths,
  monthlyPrice: res.monthlyPrice,
  note: res.note ?? null,
  items: res.items
});

export const transformFleetVehicleOfferRentOnUpdate = (
  offerId: string,
  formValues: FleetVehicleOfferRentEditFormValues
): any => ({
  id: offerId,
  offerRentStatusId: formValues.offerRentStatusId,
  vehicleId: formValues.vehicleId,
  contactClientId: formValues.contactClientId,
  contactConsumerId: formValues.contactConsumerId,
  offerDate: formValues.offerDate,
  dueDate: formValues.dueDate,
  durationDays: formValues.durationDays,
  note: formValues.note,
  rentStartDate: formValues.rentStartDate,
  rentEndDate: formValues.rentEndDate,
  rentDurationMonths: formValues.rentDurationMonths,
  monthlyPrice: formValues.monthlyPrice,
  items: formValues.items,
});


export const transformOfferRentItemRow = (
  res: FleetVehicleOfferRentItemEditFormValues
): FleetVehicleOfferRentItemRow => {
  return {
    id: res.id ? res.id : crypto.randomUUID(),
    code: res.code,
    name: res.name,
    taxRate: res.taxRate,
    quantity: res.quantity,
    priceWithoutTax: res.priceWithoutTax,
    priceWithTax: res.priceWithTax,
    discountAmount: res.discountAmount,
    discountTypeId: res.discountTypeId,
    total: res.total,
    deleted: false,
    billingItemTypeId: res.billingItemTypeId,
    billingItemTypeName: res.billingItemTypeName ?? null,
    kpdCode: res.kpdCode,
    unitOfMeasureId: res.unitOfMeasureId,
    note: res.note,
    childRows: res.note
      ? [transformOfferRentItemChildRow(res)]
      : [],
  };
};

export const transformOfferRentItemChildRow = (
  res: FleetVehicleOfferRentItemEditFormValues
): FleetVehicleOfferRentItemChildRow => {
  return {
    note: res.note,
  };
};


export const transformOfferRentItemRowToForm = (
  row: FleetVehicleOfferRentItemRow
): FleetVehicleOfferRentItemEditFormValues => {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    taxRate: row.taxRate,
    quantity: row.quantity,
    priceWithoutTax: row.priceWithoutTax,
    priceWithTax: row.priceWithTax,
    discountAmount: row.discountAmount,
    discountTypeId: row.discountTypeId,
    total: row.total,
    note: row.note ?? row.childRows?.[0]?.note ?? null,
    billingItemTypeId: row.billingItemTypeId,
    billingItemTypeName: row.billingItemTypeName ?? null,
    kpdCode: row.kpdCode,
    unitOfMeasureId: row.unitOfMeasureId,
  };
};
